import {
  forwardRef,
  useEffect,
  useImperativeHandle,
  useRef,
  type RefObject,
} from 'react'
import Vditor from 'vditor'
import 'vditor/dist/index.css'
import { VDITOR_ASSET_BASE, VDITOR_CONTENT_THEME } from '../lib/vditorAssets'
import { isDocumentBodyEmpty } from '../lib/documentBodyEmpty'
import { scrollEditorToEnd } from '../lib/scrollEditorToEnd'
import { selectTextInContainer } from '../lib/selectTextInContainer'
import {
  captureEditorCursor,
  restoreEditorCursor,
  getAnchorClientRect,
  getTextBeforeAnchor,
  getTextAfterAnchor,
  resolveMarkdownCursorSplit,
  type EditorCursorAnchor,
} from '../lib/editorCursorAnchor'
import './MarkdownDocEditor.css'

export type MarkdownDocEditorHandle = {
  getSelectionText: () => string
  getDocumentContent: () => string
  selectText: (text: string) => boolean
  insertContent: (markdown: string, mode: 'cursor' | 'append' | 'replace' | 'afterSelection') => void
  setDocumentContent: (markdown: string) => void
  saveCursorAnchor: () => boolean
  restoreCursorAnchor: () => boolean
  getSavedAnchorRect: () => DOMRect | null
  getContentBeforeCursor: () => string
  getContentAfterCursor: () => string
}

type Props = {
  /** 仅作初次挂载内容；挂载后不再受控回写，避免 Vditor 延迟空 input 清空正文 */
  value: string
  onChange: (markdown: string) => void
  onBodyEmptyChange?: (empty: boolean) => void
}

function collapseDomSelectionToEnd(host: HTMLElement | null) {
  const sel = window.getSelection()
  if (!sel || sel.rangeCount === 0) return
  const range = sel.getRangeAt(0)
  if (host && !host.contains(range.commonAncestorContainer)) return
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
}

function focusIrEditorEnd(host: HTMLElement | null, vd: Vditor) {
  vd.focus()
  const pre = host?.querySelector('.vditor-ir pre.vditor-reset')
  if (!pre) return
  const sel = window.getSelection()
  if (!sel) return
  const range = document.createRange()
  range.selectNodeContents(pre)
  range.collapse(false)
  sel.removeAllRanges()
  sel.addRange(range)
}

function getIrPlainText(host: HTMLElement | null): string {
  if (!host) return ''
  const pre = host.querySelector('.vditor-ir pre.vditor-reset')
  return (pre?.textContent ?? '').replace(/\u200b/g, '').trim()
}

function disposeVditorInstance(vditor: Vditor | null, host: HTMLElement | null, initialized: boolean) {
  if (!vditor) return
  if (initialized) {
    try {
      vditor.destroy()
    } catch {
      /* 初始化未完成或 DOM 已移除时，destroy 可能抛错 */
    }
  }
  if (host) host.replaceChildren()
}

export const MarkdownDocEditor = forwardRef<MarkdownDocEditorHandle, Props>(
  function MarkdownDocEditor({ value, onChange, onBodyEmptyChange }, ref) {
    const hostRef = useRef<HTMLDivElement>(null)
    const vditorRef = useRef<Vditor | null>(null)
    const onChangeRef = useRef(onChange)
    const onBodyEmptyChangeRef = useRef(onBodyEmptyChange)
    const committedRef = useRef(value)
    const readyRef = useRef(false)
    const userHasEditedRef = useRef(false)
    const suppressInputRef = useRef(false)
    const pendingExternalValueRef = useRef<string | null>(null)
    const cursorAnchorRef = useRef<EditorCursorAnchor | null>(null)

    const getEditableRoot = () =>
      hostRef.current?.querySelector('.vditor-ir pre.vditor-reset') as HTMLElement | null

    const applyValueToEditorRef = useRef((next: string, notifyParent: boolean) => {
      void next
      void notifyParent
    })

    const emitBodyEmptyRef = useRef(() => {})

    const applyExternalValue = (vd: Vditor, next: string) => {
      suppressInputRef.current = true
      try {
        vd.setValue(next, true)
      } finally {
        suppressInputRef.current = false
      }
      committedRef.current = next
    }

    emitBodyEmptyRef.current = () => {
      const vd = vditorRef.current
      const md = vd?.getValue() ?? committedRef.current
      const irPlain = getIrPlainText(hostRef.current)
      onBodyEmptyChangeRef.current?.(isDocumentBodyEmpty(md, 'markdown', irPlain))
    }

    const publishEditorValue = (next: string) => {
      if (suppressInputRef.current) return

      const irPlain = getIrPlainText(hostRef.current)

      // 未编辑前忽略 Vditor 延迟触发的空 input（刷新后正文被清空的根因）
      if (!next.trim() && committedRef.current.trim() && !userHasEditedRef.current) {
        emitBodyEmptyRef.current()
        return
      }

      if (!next.trim() && committedRef.current.trim()) {
        if (irPlain) {
          emitBodyEmptyRef.current()
          return
        }
      }

      if (next === committedRef.current) {
        emitBodyEmptyRef.current()
        return
      }

      userHasEditedRef.current = true
      committedRef.current = next
      onChangeRef.current(next)
      emitBodyEmptyRef.current()
    }

    applyValueToEditorRef.current = (next: string, notifyParent: boolean) => {
      if (notifyParent) {
        committedRef.current = next
        userHasEditedRef.current = next.trim().length > 0
        onChangeRef.current(next)
        onBodyEmptyChangeRef.current?.(
          isDocumentBodyEmpty(next, 'markdown', getIrPlainText(hostRef.current)),
        )
      }
      const vd = vditorRef.current
      if (!vd || !readyRef.current) {
        pendingExternalValueRef.current = next
        return
      }
      pendingExternalValueRef.current = null
      applyExternalValue(vd, next)
      emitBodyEmptyRef.current()
    }

    useEffect(() => {
      onChangeRef.current = onChange
    }, [onChange])

    useEffect(() => {
      onBodyEmptyChangeRef.current = onBodyEmptyChange
    }, [onBodyEmptyChange])

    const syncEditorValueOnly = () => {
      const vd = vditorRef.current
      if (!vd) return
      const next = vd.getValue()
      committedRef.current = next
      userHasEditedRef.current = true
      onChangeRef.current(next)
      onBodyEmptyChangeRef.current?.(
        isDocumentBodyEmpty(next, 'markdown', getIrPlainText(hostRef.current)),
      )
      emitBodyEmptyRef.current()
    }

    useImperativeHandle(
      ref,
      () => ({
        getSelectionText: () => {
          if (!readyRef.current) return ''
          return vditorRef.current?.getSelection()?.trim() || ''
        },
        getDocumentContent: () => {
          if (!readyRef.current) return committedRef.current
          return vditorRef.current?.getValue() ?? committedRef.current
        },
        selectText: (text: string) => {
          const pre = hostRef.current?.querySelector('.vditor-ir pre.vditor-reset') as HTMLElement | null
          const ok = selectTextInContainer(pre, text)
          if (ok) vditorRef.current?.focus()
          return ok
        },
        insertContent: (markdown: string, mode) => {
          const vd = vditorRef.current
          const host = hostRef.current
          const chunk = markdown.trim()
          if (!vd || !chunk || !readyRef.current) return

          if (mode === 'append') {
            focusIrEditorEnd(host, vd)
            suppressInputRef.current = true
            try {
              const cur = vd.getValue()
              vd.insertMD(cur.trim() ? `\n\n${chunk}` : chunk)
            } finally {
              suppressInputRef.current = false
            }
            syncEditorValueOnly()
            focusIrEditorEnd(host, vd)
            scrollEditorToEnd(host)
            vd.focus()
            return
          }

          vd.focus()
          if (mode === 'afterSelection') {
            collapseDomSelectionToEnd(host)
            suppressInputRef.current = true
            try {
              vd.insertMD(`\n\n${chunk}`)
            } finally {
              suppressInputRef.current = false
            }
            syncEditorValueOnly()
            vd.focus()
            return
          }
          if (mode === 'replace' && vd.getSelection()) {
            vd.deleteValue()
          }
          suppressInputRef.current = true
          try {
            vd.insertMD(chunk)
          } finally {
            suppressInputRef.current = false
          }
          syncEditorValueOnly()
          vd.focus()
        },
        setDocumentContent: (markdown: string) => {
          applyValueToEditorRef.current(markdown, true)
        },
        saveCursorAnchor: () => {
          const root = getEditableRoot()
          const anchor = captureEditorCursor(root)
          cursorAnchorRef.current = anchor
          return !!anchor
        },
        restoreCursorAnchor: () => {
          const root = getEditableRoot()
          const ok = restoreEditorCursor(cursorAnchorRef.current, root)
          if (ok) vditorRef.current?.focus()
          return ok
        },
        getSavedAnchorRect: () => getAnchorClientRect(cursorAnchorRef.current),
        getContentBeforeCursor: () => {
          const root = getEditableRoot()
          const plainBefore = getTextBeforeAnchor(cursorAnchorRef.current, root)
          const plainAfter = getTextAfterAnchor(cursorAnchorRef.current, root)
          const full = vditorRef.current?.getValue() ?? committedRef.current
          return resolveMarkdownCursorSplit(full, plainBefore, plainAfter).before
        },
        getContentAfterCursor: () => {
          const root = getEditableRoot()
          const plainBefore = getTextBeforeAnchor(cursorAnchorRef.current, root)
          const plainAfter = getTextAfterAnchor(cursorAnchorRef.current, root)
          const full = vditorRef.current?.getValue() ?? committedRef.current
          return resolveMarkdownCursorSplit(full, plainBefore, plainAfter).after
        },
      }),
      [],
    )

    useEffect(() => {
      const host = hostRef.current
      if (!host) return

      committedRef.current = value
      userHasEditedRef.current = false

      let disposed = false
      let initialized = false
      let resizeObserver: ResizeObserver | null = null

      const resolveHeight = () => Math.max(host.clientHeight, 360)

      const vditor = new Vditor(host, {
        mode: 'ir',
        lang: 'zh_CN',
        cdn: VDITOR_ASSET_BASE,
        icon: 'ant',
        typewriterMode: false,
        height: resolveHeight(),
        placeholder: '输入正文；// 唤起 AI 在光标处写作',
        cache: { enable: false },
        toolbarConfig: { pin: true },
        preview: {
          maxWidth: 9999,
          theme: { current: 'ant-design', path: VDITOR_CONTENT_THEME },
        },
        toolbar: [
          'headings',
          'bold',
          'italic',
          'strike',
          '|',
          'line',
          'quote',
          'list',
          'ordered-list',
          'check',
          '|',
          'code',
          'inline-code',
          'link',
          'table',
          '|',
          'undo',
          'redo',
        ],
        input: (v) => {
          const vd = vditorRef.current
          publishEditorValue(vd?.getValue() ?? v)
        },
        after: () => {
          initialized = true
          if (disposed) {
            disposeVditorInstance(vditor, host, true)
            return
          }
          readyRef.current = true

          const latest = pendingExternalValueRef.current ?? committedRef.current ?? ''
          pendingExternalValueRef.current = null
          if (latest) {
            applyExternalValue(vditor, latest)
          }

          queueMicrotask(() => {
            if (disposed) return
            const current = committedRef.current || ''
            if (current && vditor.getValue() !== current) {
              applyExternalValue(vditor, current)
            }
            emitBodyEmptyRef.current()
          })

          const root = host.querySelector('.vditor') as HTMLElement | null
          if (root && typeof ResizeObserver !== 'undefined') {
            resizeObserver = new ResizeObserver(() => {
              const nextHeight = resolveHeight()
              if (nextHeight > 0) root.style.height = `${nextHeight}px`
            })
            resizeObserver.observe(host)
          }
        },
      })

      vditorRef.current = vditor

      return () => {
        disposed = true
        readyRef.current = false
        resizeObserver?.disconnect()
        disposeVditorInstance(vditor, host, initialized)
        vditorRef.current = null
      }
    }, [])

    return (
      <div className="markdown-doc-editor" data-color-mode="light">
        <div ref={hostRef} className="markdown-doc-editor-host" />
      </div>
    )
  },
)

export function MarkdownDocViewer({ value }: { value: string }) {
  const previewRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    const el = previewRef.current
    if (!el) return
    void Vditor.preview(el, value || '', {
      mode: 'light',
      lang: 'zh_CN',
      cdn: VDITOR_ASSET_BASE,
      theme: { current: 'ant-design', path: VDITOR_CONTENT_THEME },
    })
  }, [value])

  return (
    <div className="markdown-doc-editor markdown-doc-editor--view" data-color-mode="light">
      <div ref={previewRef} className="markdown-doc-viewer vditor-reset" />
    </div>
  )
}

export function useMarkdownDocEditorRef(): RefObject<MarkdownDocEditorHandle | null> {
  return useRef<MarkdownDocEditorHandle | null>(null)
}
